import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { AlertTriangle } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

export function useDeadlineAlerts(enabled = true) {
  const query = trpc.kanban.deadlineAlerts.useQuery(undefined, {
    enabled,
    refetchInterval: 60_000,
  });

  const alerts = useMemo(() => {
    const now = new Date();
    const overdue: typeof query.data = [];
    const expiring3: typeof query.data = [];
    for (const v of query.data ?? []) {
      const updated = new Date(v.vacancy.updatedAt);
      const created = new Date(v.vacancy.createdAt);
      const deadlineWasUpdated = updated.getTime() - created.getTime() > 60_000;
      const updatedToday =
        now.getTime() - updated.getTime() < 24 * 60 * 60 * 1000;
      if (deadlineWasUpdated && updatedToday) continue;
      const dl = new Date(v.vacancy.deadlineDate);
      const daysLeft = Math.ceil(
        (dl.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (daysLeft < 0) overdue.push(v);
      else if (daysLeft <= 3) expiring3.push(v);
    }
    return { overdue, expiring3 };
  }, [query.data]);

  return {
    ...query,
    alerts,
    totalAlerts: alerts.overdue.length + alerts.expiring3.length,
  };
}

type DeadlineAlerts = ReturnType<typeof useDeadlineAlerts>["alerts"];

export function DeadlineAlertsPanel({
  alerts,
  onSelect,
  className,
  variant,
}: {
  alerts: DeadlineAlerts;
  onSelect: (vacancyId: number) => void;
  className?: string;
  variant?: "hr";
}) {
  const renderItem = (
    v: NonNullable<ReturnType<typeof useDeadlineAlerts>["data"]>[number],
    urgency: "overdue" | "soon",
    label: string
  ) => {
    const recruiterName = v.recruiter?.displayName || v.recruiter?.name || "—";
    const contractDate = v.contract?.contractDate
      ? new Date(v.contract.contractDate)
      : null;
    const daysInWork = contractDate
      ? Math.floor((Date.now() - contractDate.getTime()) / 86400000)
      : 0;
    const fmtDate = contractDate
      ? `${contractDate.getDate().toString().padStart(2, "0")}.${(contractDate.getMonth() + 1).toString().padStart(2, "0")}`
      : "—";
    return (
      <button
        key={v.vacancy.id}
        onClick={() => onSelect(v.vacancy.id)}
        className={
          variant === "hr"
            ? `hr-notification-deadline-item${urgency === "overdue" ? " is-overdue" : " is-soon"}`
            : "w-full flex items-center gap-2 px-2 py-2 rounded-md hover:bg-slate-50 text-left transition-colors"
        }
      >
        <div
          className={
            variant === "hr"
              ? "hr-notification-deadline-copy"
              : "flex-1 min-w-0"
          }
        >
          <p
            className={
              variant === "hr"
                ? "hr-notification-deadline-title"
                : "text-[13px] leading-snug font-medium text-ink-title"
            }
          >
            {v.vacancy.positionTitle}
          </p>
          <p
            className={
              variant === "hr"
                ? "hr-notification-deadline-caption"
                : "text-[11px] text-ink-secondary"
            }
          >
            {v.company?.name ?? "—"} ·{" "}
            <span className="font-semibold">{recruiterName}</span>
          </p>
          <p
            className={
              variant === "hr"
                ? "hr-notification-deadline-caption"
                : "text-[11px] text-ink-secondary"
            }
          >
            от {fmtDate} · {daysInWork} дн. в работе
          </p>
        </div>
        <span
          className={
            variant === "hr"
              ? "hr-notification-deadline-label"
              : `text-[11px] font-semibold shrink-0 whitespace-nowrap ${urgency === "overdue" ? "text-red-500" : "text-amber-600"}`
          }
        >
          {label}
        </span>
      </button>
    );
  };

  return (
    <div
      className={`${variant === "hr" ? "hr-notification-deadline-list" : ""} overflow-y-auto ${className ?? "max-h-[420px]"}`}
    >
      {alerts.overdue.length > 0 && (
        <div className="p-2">
          <p
            className={
              variant === "hr"
                ? "hr-notification-deadline-group-title is-overdue"
                : "text-[11px] font-semibold text-red-600 px-2 py-1"
            }
          >
            Просрочено ({alerts.overdue.length})
          </p>
          {alerts.overdue.map(
            (
              v: NonNullable<
                ReturnType<typeof useDeadlineAlerts>["data"]
              >[number]
            ) => {
              const d = Math.abs(
                Math.ceil(
                  (new Date(v.vacancy.deadlineDate).getTime() - Date.now()) /
                    86400000
                )
              );
              return renderItem(v, "overdue", `−${d} дн.`);
            }
          )}
        </div>
      )}
      {alerts.expiring3.length > 0 && (
        <div className={`p-2 ${alerts.overdue.length > 0 ? "border-t" : ""}`}>
          <p
            className={
              variant === "hr"
                ? "hr-notification-deadline-group-title is-soon"
                : "text-[11px] font-semibold text-amber-600 px-2 py-1"
            }
          >
            Ближайшие 3 дня ({alerts.expiring3.length})
          </p>
          {alerts.expiring3.map(
            (
              v: NonNullable<
                ReturnType<typeof useDeadlineAlerts>["data"]
              >[number]
            ) => {
              const d = Math.ceil(
                (new Date(v.vacancy.deadlineDate).getTime() - Date.now()) /
                  86400000
              );
              return renderItem(
                v,
                "soon",
                d === 0 ? "Сегодня" : `Через ${d} дн.`
              );
            }
          )}
        </div>
      )}
      {alerts.overdue.length === 0 && alerts.expiring3.length === 0 && (
        <div
          className={
            variant === "hr"
              ? "hr-notification-state"
              : "px-4 py-10 text-center text-[13px] text-ink-secondary"
          }
        >
          Срочных дедлайнов нет
        </div>
      )}
    </div>
  );
}

export function DeadlineAlertBell() {
  const [open, setOpen] = useState(false);
  const [, navigate] = useLocation();
  const { alerts, totalAlerts, isLoading, isError } = useDeadlineAlerts();

  if (totalAlerts === 0) return null;

  const hasOverdue = alerts.overdue.length > 0;
  const handleClick = (vacancyId: number) => {
    navigate(`/kanban?vacancy=${vacancyId}`);
    window.dispatchEvent(
      new CustomEvent("open-vacancy", { detail: { vacancyId } })
    );
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Дедлайны вакансий: ${totalAlerts}`}
          className="relative size-8 rounded-lg hover:bg-neutral-100"
        >
          <AlertTriangle
            className={`size-4 ${hasOverdue ? "text-red-500" : "text-amber-500"}`}
          />
          {totalAlerts > 0 && (
            <span className="absolute right-1 top-1 flex items-center justify-center rounded-full bg-red-500 text-[9px] font-semibold text-white min-w-[14px] h-3.5 px-0.5 border border-white">
              {totalAlerts > 99 ? "99+" : totalAlerts}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(420px,calc(100vw-24px))] p-0 max-h-[50vh] overflow-hidden flex flex-col rounded-2xl shadow-[0_8px_30px_-4px_rgba(0,0,0,0.12)] border-0"
        align="end"
        collisionPadding={12}
      >
        <div className="px-4 py-3 bg-slate-50/60 shrink-0 rounded-t-2xl">
          <h3 className="font-semibold text-sm text-ink-title">
            Дедлайны вакансий
          </h3>
        </div>
        {isLoading ? (
          <div className="px-4 py-10 text-center text-[13px] text-ink-secondary">
            Загружаем дедлайны…
          </div>
        ) : isError ? (
          <div className="px-4 py-10 text-center text-[13px] text-ink-secondary">
            Не удалось загрузить дедлайны
          </div>
        ) : (
          <DeadlineAlertsPanel
            alerts={alerts}
            onSelect={handleClick}
            className="flex-1 min-h-0"
          />
        )}
      </PopoverContent>
    </Popover>
  );
}
